import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = '';

function ComparisonComponent() {
  const [mfr1, setMfr1] = useState('Ford');
  const [mfr2, setMfr2] = useState('Honda');
  const [data1, setData1] = useState(null);
  const [data2, setData2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/api/manufacturers`)
      .then(res => setManufacturers(res.data.manufacturers || []))
      .catch(() => setManufacturers(['Ford','Honda','Toyota','Chevrolet','Hyundai','Jeep','Nissan','Acura']));
  }, []);

  useEffect(() => { loadComparison(); }, [mfr1, mfr2]);

  const loadComparison = async () => {
    setLoading(true);
    try {
      const [res1, res2] = await Promise.all([
        axios.get(`${API_BASE}/api/manufacturer/${mfr1}`),
        axios.get(`${API_BASE}/api/manufacturer/${mfr2}`)
      ]);
      setData1(res1.data);
      setData2(res2.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const getColor = (score) => {
    if (score >= 80) return '#22c55e';
    if (score >= 60) return '#ca8a04';
    if (score >= 40) return '#ea580c';
    return '#ef4444';
  };

  const getWinner = (v1, v2, higher = true) => {
    if (v1 === v2) return 'tie';
    return higher ? (v1 > v2 ? 'left' : 'right') : (v1 < v2 ? 'left' : 'right');
  };

  if (loading || !data1 || !data2) return <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div>;

  const cpv1 = data1.complaints_per_vehicle || (data1.total_complaints / Math.max(1, data1.total_vehicles));
  const cpv2 = data2.complaints_per_vehicle || (data2.total_complaints / Math.max(1, data2.total_vehicles));

  const metrics = [
    { label: 'Avg Reliability', val1: `${data1.average_reliability}%`, val2: `${data2.average_reliability}%`, winner: getWinner(data1.average_reliability, data2.average_reliability) },
    { label: 'Total Vehicles', val1: data1.total_vehicles.toLocaleString(), val2: data2.total_vehicles.toLocaleString(), winner: 'tie' },
    { label: 'Total Complaints', val1: data1.total_complaints.toLocaleString(), val2: data2.total_complaints.toLocaleString(), winner: getWinner(data1.total_complaints, data2.total_complaints, false) },
    { label: 'Complaints / Vehicle', val1: cpv1.toFixed(1), val2: cpv2.toFixed(1), winner: getWinner(cpv1, cpv2, false) },
    { label: 'Avg MTTF (years)', val1: data1.average_mttf ? `${data1.average_mttf} yr` : 'N/A', val2: data2.average_mttf ? `${data2.average_mttf} yr` : 'N/A', winner: data1.average_mttf && data2.average_mttf ? getWinner(data1.average_mttf, data2.average_mttf) : 'tie' }
  ];

  let overallWinner = null, overallLoser = null, winnerCpv = 0, loserCpv = 0;
  if (cpv1 < cpv2) { overallWinner = mfr1; overallLoser = mfr2; winnerCpv = cpv1; loserCpv = cpv2; }
  else if (cpv2 < cpv1) { overallWinner = mfr2; overallLoser = mfr1; winnerCpv = cpv2; loserCpv = cpv1; }

  const card = {background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.06)',borderRadius:'12px',padding:'24px'};
  const mfrs = manufacturers.length > 0 ? manufacturers : ['Ford','Honda','Toyota','Chevrolet'];

  return (
    <div className="comparison-section">
      <h2 style={{marginBottom:'24px',color:'#f1f5f9'}}>Manufacturer Comparison</h2>
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'16px',marginBottom:'32px'}}>
        <select value={mfr1} onChange={e => setMfr1(e.target.value)} className="comparison-select">
          {mfrs.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
        </select>
        <span style={{fontWeight:'700',fontSize:'18px',color:'#94a3b8'}}>vs</span>
        <select value={mfr2} onChange={e => setMfr2(e.target.value)} className="comparison-select">
          {mfrs.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
        </select>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'24px',marginBottom:'32px'}}>
        {[{data:data1,name:mfr1,r:data1.average_reliability},{data:data2,name:mfr2,r:data2.average_reliability}].map((s,i) => (
          <div key={i} style={{...card,borderTop:`4px solid ${getColor(s.r)}`}}>
            <h3 style={{margin:'0 0 16px',textTransform:'capitalize',fontSize:'20px',color:'#94a3b8'}}>{s.name}</h3>
            <div style={{fontSize:'36px',fontWeight:'800',color:getColor(s.r),marginBottom:'16px'}}>
              {s.r}% <span style={{fontSize:'14px',fontWeight:'400',color:'#64748b',marginLeft:'8px'}}>reliability</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div><div style={{fontSize:'11px',color:'#64748b',textTransform:'uppercase'}}>Vehicles</div><div style={{fontSize:'18px',fontWeight:'600',color:'#e2e8f0'}}>{s.data.total_vehicles.toLocaleString()}</div></div>
              <div><div style={{fontSize:'11px',color:'#64748b',textTransform:'uppercase'}}>Complaints</div><div style={{fontSize:'18px',fontWeight:'600',color:'#e2e8f0'}}>{s.data.total_complaints.toLocaleString()}</div></div>
            </div>
          </div>
        ))}
      </div>
      <div style={{...card,overflow:'hidden',marginBottom:'24px',padding:0}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 2fr 1fr',padding:'12px 20px',background:'rgba(255,255,255,.05)',fontWeight:'600',fontSize:'13px',color:'#94a3b8',textTransform:'uppercase'}}>
          <div style={{textTransform:'capitalize'}}>{mfr1}</div>
          <div style={{textAlign:'center'}}>Metric</div>
          <div style={{textAlign:'right',textTransform:'capitalize'}}>{mfr2}</div>
        </div>
        {metrics.map((m,i) => (
          <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 2fr 1fr',padding:'14px 20px',borderTop:'1px solid rgba(255,255,255,.06)',alignItems:'center'}}>
            <div style={{fontWeight:m.winner==='left'?'700':'400',color:m.winner==='left'?'#22c55e':'#e2e8f0',fontSize:'15px'}}>{m.winner==='left'&&'\u2713 '}{m.val1}</div>
            <div style={{textAlign:'center',color:'#94a3b8',fontSize:'13px'}}>{m.label}</div>
            <div style={{textAlign:'right',fontWeight:m.winner==='right'?'700':'400',color:m.winner==='right'?'#22c55e':'#e2e8f0',fontSize:'15px'}}>{m.val2}{m.winner==='right'&&' \u2713'}</div>
          </div>
        ))}
      </div>
      {overallWinner && (
        <div style={{background:'rgba(34,197,94,.08)',borderLeft:'4px solid #22c55e',borderRadius:'8px',padding:'16px 20px'}}>
          <div style={{fontWeight:'700',marginBottom:'4px',color:'#22c55e'}}>Key Finding</div>
          <div style={{color:'#e2e8f0'}}>
            <span style={{textTransform:'capitalize',fontWeight:'600'}}>{overallWinner}</span> averages {winnerCpv.toFixed(1)} complaints per vehicle vs {loserCpv.toFixed(1)} for <span style={{textTransform:'capitalize'}}>{overallLoser}</span>, indicating fewer reported issues per vehicle in the NHTSA dataset.
          </div>
        </div>
      )}
      <div style={{textAlign:"center",color:"#64748b",fontSize:"12px",marginTop:"24px",padding:"12px"}}>Disclaimer: Data sourced from NHTSA complaint database. For experimental and educational purposes only, not intended for commercial use.</div>
    </div>
  );
}

export default ComparisonComponent;
